import type { CompanyRankings } from '@/api/v4/organization-ratings.api';
import {
  addCompanyRanking,
  removeCompanyRanking,
} from '@/api/v4/organization-ratings.api';
import { QueryKey } from '@/config/query-client';
import { notify } from '@/ui/snackbar/notify';
import { useCompanyFetchMode } from '../company-fetch-context';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

export const useAddCompanyRanking = (companyId: string | null) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('default');
  const fetchMode = useCompanyFetchMode();

  const queryKey = [QueryKey.CompanyRankings, fetchMode, companyId];
  const companyQueryKey = [QueryKey.Company, fetchMode, companyId];

  const onErrorHandler = (
    _err: unknown,
    _var: unknown,
    context:
      | {
          previousCompanyRatings: unknown;
        }
      | undefined,
  ) => {
    queryClient.setQueryData(queryKey, context?.previousCompanyRatings);

    notify({
      message: t`unknownError`,
    });
  };

  const onSettledHandler = () => {
    return queryClient.invalidateQueries({
      queryKey,
    });
  };

  return {
    addRating: useMutation({
      mutationFn: addCompanyRanking,
      onMutate: async userRating => {
        // (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({
          queryKey,
        });

        const previousCompanyRatings = queryClient.getQueryData(queryKey);

        queryClient.setQueryData<CompanyRankings>(queryKey, old => {
          if (!old?.[userRating.type]) return old;

          return {
            ...old,
            [userRating.type]: {
              averageRating: old[userRating.type]?.averageRating ?? null,
              userRating: userRating.rating,
            },
          };
        });

        return { previousCompanyRatings };
      },
      onError: onErrorHandler,
      onSettled: onSettledHandler,
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: companyQueryKey }),
          queryClient.invalidateQueries({
            queryKey: [QueryKey.LastActivities],
          }),
          queryClient.invalidateQueries({
            queryKey: [QueryKey.LastActivitiesCount],
          }),
        ]);
      },
    }),
    removeRating: useMutation({
      mutationFn: removeCompanyRanking,
      onMutate: async removedRating => {
        await queryClient.cancelQueries({
          queryKey,
        });

        const previousCompanyRatings = queryClient.getQueryData(queryKey);

        queryClient.setQueryData<CompanyRankings>(queryKey, old => {
          if (!old?.[removedRating.type]) return old;

          return {
            ...old,
            [removedRating.type]: {
              averageRating: old[removedRating.type]?.averageRating ?? null,
              userRating: null,
            },
          };
        });

        return { previousCompanyRatings };
      },
      onError: onErrorHandler,
      onSettled: onSettledHandler,
    }),
  };
};
